/**
 * 書式のエディタの右の帯：部品／明細表／用紙／色／画像（046）。
 *   数字の欄は type="text" inputmode="decimal"（type="number" は使わない＝CLAUDE.md 入力の決めごと）。決定は blur か Enter、↑↓で刻み
 *   画像（046）：上げる（PNG/JPEG。画面で 2400px・2MB に縮める）→ 一覧から「配置」か「下敷きに配置」（S-00009 で「置く」「下敷きに」から）。会社のロゴ・社印もここから置ける。
 *   使っている画像（消した書式・出した紙が指すものも）は消せない（API が 409）
 */
import { ORDER_PAPER_BINDS, PAPER_COL_LABEL, paperBindLabel, type PaperColId, PAPER_IMAGE_MAX_PX, PAPER_IMAGE_MSG, PaperBind, type PaperEl, type PaperImageList, type PaperLayout, type PaperTable } from "@genbaryoku/contract";
import { PAPER_PALETTE, cmyk2rgb, contrast, hex2rgb, overlapAllowed, r1, repeatIds, rgb2cmyk, rgb2hex, withRepeat } from "@genbaryoku/paper";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { api, messageOf } from "../../api.js";
import { Help, Switch, type ToastFn } from "../../components/ui.jsx";
import { currentLabels, t } from "../../labels.js";
import { prepareImage } from "../../lib/image.js";
import { FONTS, RULE_STYLES, RULE_W, TYPE_NAME, setOrient, setPageSize, updateEl, updateTable, type RuleStyle, type Sel } from "./editorState.js";

export type Tab = "els" | "table" | "page" | "color" | "images";

/** 画像の id → エディタが <img> で読む URL（PDF は API が data: URL にする） */
/** 列の名前（#222：名称・規格・備考は会社の言葉）。表の列の map の中では t が表を指すので、外のこの関数で埋める */
const colLabelOf = (id: PaperColId): string => t(PAPER_COL_LABEL[id]);

export function assetUrl(asset: string): string {
  if (asset === "company:logo") return "/api/v1/company/logo";
  if (asset === "company:seal") return "/api/v1/company/seal";
  // 現場の地図（#133）：エディタでは見本の絵。本物の見積のテスト印刷と出すときは API が案件の座標から地理院タイルで作る
  return `/api/v1/paper/images/${asset}`;
}
export const IMAGES_QUERY = ["paper-images"] as const;

type Props = {
  layout: PaperLayout;
  sel: Sel;
  onSelect: (s: Sel) => void;
  commit: (next: PaperLayout | ((cur: PaperLayout) => PaperLayout)) => void;
  tab: Tab;
  setTab: (t: Tab) => void;
  colorTarget: string;
  setColorTarget: (t: string) => void;
  onAdd: (kind: "text" | "field" | "line" | "vline" | "box" | "qr") => void;
  /** 画像を紙に置く（046）。guide＝下敷き */
  onAddImage: (asset: string, name: string, px: { width: number; height: number } | null, guide: boolean) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  toast: ToastFn;
  admin: boolean;
  /** 注意のある部品（#217 ■4。一覧に朱の印） */
  warnIds: Set<string>;
  /** 明細の下の部品の基準の Y（表の下端＋3mm。描き手が決めた位置） */
  dockBaseY: number;
  /** 備考の下に付ける部品（dock end、#233 2d）の基準 */
  endBaseY?: number;
};

/* ---- 数字の欄 ---- */
export function NumIn({ value, onChange, min = -999, max = 999, step = 1, width = 72, label, testId }: { value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; width?: number; label?: string; testId?: string }) {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => setTxt(String(value)), [value]);
  const commit = (raw: string) => {
    const v = Number(raw.replace(/[０-９．－]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)));
    if (raw.trim() === "" || Number.isNaN(v)) { setTxt(String(value)); return; }
    const c = Math.min(max, Math.max(min, r1(v)));
    setTxt(String(c));
    if (c !== value) onChange(c);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      className="inp pe-num"
      style={{ width }}
      value={txt}
      aria-label={label}
      data-testid={testId}
      onChange={(e) => setTxt(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); commit((e.target as HTMLInputElement).value); }
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          const cur = Number(txt) || value;
          commit(String(r1(cur + (e.key === "ArrowUp" ? step : -step))));
        }
        e.stopPropagation();
      }}
    />
  );
}

/** {order}の書式に出さない項目（有効期限・振込先・分割の欄・領収書の項目・工事概要。#233 2d） */
const ORDER_EXCLUDE: ReadonlySet<PaperBind> = new Set<PaperBind>(["valid", "bank", "installment", "proviso", "invoiceNo", "outline"]);

function Seg<T extends string | boolean>({ value, options, onChange, label }: { value: T; options: Array<[T, string]>; onChange: (v: T) => void; label: string }) {
  return (
    <span className="seg sm" role="group" aria-label={label}>
      {options.map(([v, n]) => (
        <button key={String(v)} type="button" aria-pressed={value === v} onClick={() => onChange(v)}>{n}</button>
      ))}
    </span>
  );
}

/** 色の四角（押すと「色」タブでその色を直す） */
function Swatch({ value, target, setColorTarget, setTab, label }: { value: string; target: string; setColorTarget: (t: string) => void; setTab: (t: Tab) => void; label: string }) {
  return (
    <button type="button" className="pe-sw" aria-label={`${label}の色を選択`} title={value || "無し"} onClick={() => { setColorTarget(target); setTab("color"); }}>
      <i style={value ? { background: value } : { background: "repeating-linear-gradient(45deg,#ddd 0 4px,#fff 4px 8px)" }} />
    </button>
  );
}

/** 色の対象（path）→ いまの値／書き込み */
export function colorGet(layout: PaperLayout, sel: Sel, target: string): string {
  if (target.startsWith("el.")) {
    const el = layout.els.find((e) => e.id === sel);
    return (el as unknown as Record<string, string> | undefined)?.[target.slice(3)] ?? "";
  }
  return String(getPath(layout, target) ?? "");
}
export function colorSet(layout: PaperLayout, sel: Sel, target: string, v: string): PaperLayout {
  if (target.startsWith("el.")) return sel ? updateEl(layout, sel, { [target.slice(3)]: v } as Partial<PaperEl>) : layout;
  return setPath(layout, target, v);
}
function getPath(o: unknown, p: string): unknown {
  return p.split(".").reduce<unknown>((a, k) => (a == null ? a : (a as Record<string, unknown>)[k]), o);
}
function setPath<T>(o: T, p: string, v: unknown): T {
  const ks = p.split(".");
  const rec = (cur: unknown, i: number): unknown => {
    if (i === ks.length) return v;
    const k = ks[i]!;
    if (Array.isArray(cur)) { const c = [...cur]; c[Number(k)] = rec(cur[Number(k)], i + 1); return c; }
    const c = { ...(cur as Record<string, unknown>) };
    c[k] = rec(c[k], i + 1);
    return c;
  };
  return rec(o, 0) as T;
}
const COLOR_TARGET_NAME: Record<string, string> = {
  "table.color": "明細の文字", "table.header.color": "明細の見出しの文字", "table.header.fill": "明細の見出しの地色", "table.zebra": "明細の1行おきの地色",
  "table.rules.outer.color": "明細の外枠", "table.rules.head.color": "明細の見出しの下の線", "table.rules.h.color": "明細の行の間の線", "table.rules.v.color": "明細の列の間の線",
};

export function PaperPane(p: Props) {
  const { layout, sel, commit, tab, setTab, setColorTarget } = p;
  /** 「？」の文は言葉と製品の言い回しを通す（#142 段6 No.40・41：{pw:formsFrom}＝元請／取引先） */
  const H = (text: string) => <Help text={t(text)} />;
  const sw = (target: string, label: string) => <Swatch value={colorGet(layout, sel, target)} target={target} setColorTarget={setColorTarget} setTab={setTab} label={label} />;

  const tabs: Array<[Tab, string]> = [["els", "部品"], ["table", "明細表"], ["page", "用紙"], ["color", "色"], ["images", "画像"]];
  return (
    <div className="pe-pane">
      <div className="pe-tabs" role="tablist">
        {tabs.map(([k, n]) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{n}</button>
        ))}
      </div>
      <div className="pe-panebody">
        {tab === "els" ? <ElsTab {...p} H={H} sw={sw} /> : null}
        {tab === "table" ? <TableTab {...p} H={H} sw={sw} /> : null}
        {tab === "page" ? <PageTab {...p} H={H} /> : null}
        {tab === "color" ? <ColorTab {...p} H={H} /> : null}
        {tab === "images" ? <ImagesTab {...p} H={H} /> : null}
      </div>
    </div>
  );
}

/* ---- 部品 ---- */
/** 線の太さの選択肢（プリセットの線 0.26・0.53 などもそのまま選べるように、今の値を足す） */
const ruleWidths = (cur: number): number[] => (RULE_W.includes(cur) ? RULE_W : [...RULE_W, cur].sort((a, b) => a - b));

function ElsTab({ layout, sel, onSelect, commit, H, sw, onAdd, onDuplicate, onDelete, toast, setTab, warnIds, dockBaseY, endBaseY }: Props & { H: (t: string) => React.ReactNode; sw: (t: string, l: string) => React.ReactNode }) {
  const el = sel && sel !== "table" ? layout.els.find((e) => e.id === sel) : undefined;
  const t = layout.table;
  const [addOpen, setAddOpen] = useState(false);
  const patch = (pt: Partial<PaperEl>) => {
    if (!el) return;
    // 二次元コードは正方形（幅と高さを同じに）
    const sq = pt.w ?? pt.h;
    const p2: Partial<PaperEl> = el.type === "qr" && sq !== undefined ? { ...pt, w: sq, h: sq } : pt;
    commit((cur) => updateEl(cur, el.id, p2));
  };
  const align = (a: "l" | "c" | "r" | "t" | "b") => {
    if (!el) return;
    const { w: pw, h: ph, margin: [mt, mr, mb, ml] } = layout.page;
    const pt: Partial<PaperEl> = {};
    if (a === "l") pt.x = ml;
    if (a === "r") pt.x = r1(pw - mr - el.w);
    if (a === "c") pt.x = r1((pw - el.w) / 2);
    if (a === "t" && !el.dock) pt.y = mt;
    if (a === "b" && !el.dock) pt.y = r1(ph - mb - el.h);
    patch(pt);
  };
  const isTxt = el?.type === "text" || el?.type === "field";
  const isLine = el?.type === "line";
  const isBox = el?.type === "box";
  const y = el ? (el.dock === "table" ? dockBaseY + (el.dy ?? 0) : el.dock === "end" ? (endBaseY ?? dockBaseY) + (el.dy ?? 0) : el.y) : 0;
  /** 項目の候補：{order}だけの項目は{order}の書式だけに（#233 2d） */
  const bindOptions = PaperBind.options.filter((b) => (layout.kind === "order" ? !ORDER_EXCLUDE.has(b) : !ORDER_PAPER_BINDS.includes(b)));
  return (
    <>
      <div className="pe-row between">
        <h3>部品 {H("紙の上の枠を1つずつ動かせます。見出し（「見積番号」などの文字）と値（番号そのもの）は別の部品なので、位置・大きさ・印字を別々に決められます。🔒 は法令で消せない部品（動かすことはできます）。「組で動かす」が入っていると、同じ組の部品は一緒に動きます。明細表の高さは 見出し行＋行数×行間 で決まります。二次元コードは正方形で描きます（幅を変えると高さも同じになります）。周りの白抜きは自動で付きます。")}</h3>
        <button type="button" className="btn sm" onClick={() => setAddOpen((v) => !v)} aria-expanded={addOpen}>＋ 追加</button>
      </div>
      {addOpen ? (
        <div className="pe-addmenu">
          <button type="button" className="btn sm" onClick={() => { onAdd("text"); setAddOpen(false); }}>文字（見出し・注意書き）</button>
          <button type="button" className="btn sm" onClick={() => { onAdd("field"); setAddOpen(false); }}>項目（データ印字）</button>
          <button type="button" className="btn sm" onClick={() => { onAdd("line"); setAddOpen(false); }}>横線</button>
          <button type="button" className="btn sm" onClick={() => { onAdd("vline"); setAddOpen(false); }}>縦線</button>
          <button type="button" className="btn sm" onClick={() => { onAdd("box"); setAddOpen(false); }}>枠</button>
          <button type="button" className="btn sm" onClick={() => { setTab("images"); setAddOpen(false); }}>画像（画像タブから）</button>
          <button type="button" className="btn sm" onClick={() => { onAdd("qr"); setAddOpen(false); }}>二次元コード</button>
        </div>
      ) : null}
      <div className="pe-list" data-testid="pe-list">
        {[{ id: "table" as const, name: "明細表", type: "table" as const, on: true, locked: true, frame: false }, ...layout.els].map((x) => (
          <div key={x.id} className={"pe-it" + (x.id === sel ? " sel" : "") + (warnIds.has(x.id) ? " is-warn" : "")}>
            <input
              type="checkbox"
              checked={x.on}
              disabled={x.locked || x.id === "table"}
              aria-label={`${x.name || TYPE_NAME[x.type]}を印刷`}
              onChange={(e) => {
                if (x.id === "table") return;
                commit((cur) => updateEl(cur, x.id, { on: e.target.checked }));
              }}
            />
            <button type="button" className="pe-nm" onClick={() => onSelect(x.id)} data-testid={`pe-el-${x.id}`}>{x.name || TYPE_NAME[x.type]}</button>
            <span className="pe-ty">{TYPE_NAME[x.type]}</span>
            {warnIds.has(x.id) ? <span className="pe-wmark" title="注意があります（上の「注意」で中身）" aria-label="注意あり">⚠</span> : null}
            {x.locked || x.id === "table" ? <span className="pe-lk" title="法令上、消せません">🔒</span> : null}
            {x.frame && layout.page.preprinted ? <span className="pe-lk">枠</span> : null}
          </div>
        ))}
      </div>
      {sel === "table" ? (
        <div className="pe-detail">
          <div className="pe-row between"><b>明細表</b><button type="button" className="btn sm" onClick={() => setTab("table")}>明細表タブへ</button></div>
          <div className="pe-grid4">
            <label className="pe-f"><span>左から X</span><NumIn value={t.x} min={0} max={600} onChange={(v) => commit((c) => updateTable(c, { x: v }))} label="明細表の X" /></label>
            <label className="pe-f"><span>上から Y</span><NumIn value={t.y} min={0} max={600} onChange={(v) => commit((c) => updateTable(c, { y: v }))} label="明細表の Y" /></label>
            <label className="pe-f"><span>幅 W</span><NumIn value={t.w} min={20} max={600} onChange={(v) => commit((c) => updateTable(c, { w: v }))} label="明細表の幅" /></label>
            <label className="pe-f"><span>高さ</span><input className="inp pe-num" style={{ width: 72 }} value={r1(dockBaseY - 3 - t.y)} disabled readOnly aria-label="明細表の高さ" /></label>
          </div>
        </div>
      ) : null}
      {el ? (
        <div className="pe-detail" data-testid="pe-detail">
          <div className="pe-row between">
            <b>{el.name || TYPE_NAME[el.type]}</b>
            <span className="pe-row" style={{ gap: 4 }}>
              <button type="button" className="btn sm" onClick={onDuplicate}>複製</button>
              <button type="button" className="btn sm" onClick={onDelete} disabled={el.locked} title={el.locked ? "法令上、消せません" : ""}>削除</button>
            </span>
          </div>
          <label className="pe-f"><span>部品の名前（一覧に出る）</span><input className="inp" type="text" value={el.name} onChange={(e) => patch({ name: e.target.value })} /></label>
          <div className="pe-grid4">
            <label className="pe-f"><span>左から X</span><NumIn value={r1(el.x)} step={0.5} min={-50} max={600} onChange={(v) => patch({ x: v })} label="X" testId="pe-x" /></label>
            <label className="pe-f"><span>上から Y</span>{el.dock ? <input className="inp pe-num" style={{ width: 72 }} value={r1(y)} disabled readOnly title="明細表の下に付いています" aria-label="Y" /> : <NumIn value={r1(el.y)} step={0.5} min={-50} max={600} onChange={(v) => patch({ y: v })} label="Y" testId="pe-y" />}</label>
            <label className="pe-f"><span>{isLine && el.dir === "v" ? "—" : "幅 W"}</span>{isLine && el.dir === "v" ? <input className="inp pe-num" style={{ width: 72 }} value="0" disabled readOnly aria-label="幅" /> : <NumIn value={r1(el.w)} step={0.5} min={1} max={600} onChange={(v) => patch({ w: v })} label="幅" testId="pe-w" />}</label>
            <label className="pe-f"><span>{isLine && el.dir === "h" ? "—" : "高さ H"}</span>{isLine && el.dir === "h" ? <input className="inp pe-num" style={{ width: 72 }} value="0" disabled readOnly aria-label="高さ" /> : <NumIn value={r1(el.h)} step={0.5} min={0} max={600} onChange={(v) => patch({ h: v })} label="高さ" testId="pe-h" />}</label>
          </div>
          {el.dock ? (
            <>
              {layout.kind === "order" || el.dock === "end" ? (
                // 付ける所（#233 2d）：明細表の下／備考の下（{order}兼請書の切り取り線と請書の欄＝紙のいちばん下に）
                <Seg value={el.dock} options={[["table", "明細表の下"], ["end", "備考の下"]]} onChange={(v) => patch({ dock: v } as Partial<PaperEl>)} label="付ける所" />
              ) : null}
              <div className="pe-row">
                <label className="pe-f h"><span>{el.dock === "end" ? "備考の下から" : "明細表の下から"}</span><NumIn value={r1(el.dy ?? 0)} step={0.5} min={-50} max={300} onChange={(v) => patch({ dy: v })} label={el.dock === "end" ? "備考の下から" : "明細表の下から"} /><span className="hint">mm</span></label>
                <button type="button" className="btn sm" onClick={() => patch({ y: r1(y), dock: undefined, dy: undefined } as Partial<PaperEl>)}>位置固定</button>
              </div>
            </>
          ) : (
            <label className="pe-cb"><input type="checkbox" checked={false} onChange={() => patch({ dock: "table", dy: r1(el.y - dockBaseY) })} /> 明細表の下に付けて動かす</label>
          )}
          {!el.dock ? (
            // 2ページ目以降にも出す（2026-10-19：プリセットは番号・日付・ページの下の帳票名とページ番号に付く）。印の無い前の書式は今出している部品を印に写してから変える（withRepeat）
            <label className="pe-cb" title={layout.page.secondPage === "same" ? "2ページ目以降が「1ページ目と同じ」のときは全部の部品を出します" : undefined}>
              <input type="checkbox" checked={repeatIds(layout).has(el.id)} disabled={layout.page.secondPage === "same"} onChange={(e) => commit((cur) => withRepeat(cur, el.id, e.target.checked))} data-testid="pe-repeat" /> 2ページ目以降にも出す
            </label>
          ) : null}
          <div className="pe-row">
            <span className="hint">揃える</span>
            <button type="button" className="btn sm" onClick={() => align("l")}>左端</button>
            <button type="button" className="btn sm" onClick={() => align("c")}>中央</button>
            <button type="button" className="btn sm" onClick={() => align("r")}>右端</button>
            <button type="button" className="btn sm" onClick={() => align("t")} disabled={!!el.dock}>上端</button>
            <button type="button" className="btn sm" onClick={() => align("b")} disabled={!!el.dock}>下端</button>
          </div>
          {el.type === "text" ? (
            <label className="pe-f"><span>文言（改行できます。紙の上でダブルクリックしても修正できます）</span><textarea className="inp" rows={2} value={el.text} onChange={(e) => patch({ text: e.target.value })} data-testid="pe-text" /></label>
          ) : null}
          {el.type === "field" ? (
            <label className="pe-f"><span>印字する項目</span>
              <select className="inp" value={el.bind} onChange={(e) => patch({ bind: PaperBind.parse(e.target.value) })} disabled={el.locked} data-testid="pe-bind">
                {(bindOptions.includes(el.bind) ? bindOptions : [el.bind, ...bindOptions]).map((b) => <option key={b} value={b}>{paperBindLabel(b, currentLabels())}</option>)}
              </select>
            </label>
          ) : null}
          {el.type === "field" && layout.kind === "order" ? (
            // 見出しを刷る（#233 2d）：工事・仕入で言葉が変わる見出し（工事名⇄件名・工事場所⇄納品場所・工期⇄受領期日・注文金額⇄合計金額）
            <label className="pe-cb">
              <input type="checkbox" checked={!!el.head} disabled={el.locked} onChange={(e) => patch({ head: e.target.checked || undefined } as Partial<PaperEl>)} data-testid="pe-head" /> 項目の見出しを印字
              {H("値の代わりに項目の見出しを印字します。{order}の条件の表の見出しは、工事と仕入で言葉が変わります（工事名と件名・工事場所と納品場所・工期と受領期日・注文金額と合計金額）。")}
            </label>
          ) : null}
          {el.type === "field" ? (
            // 値の前後に付ける文字（#216：金額の「¥」「－」・番号の「No. 」）。値が空なら印字しない
            <div className="pe-grid2">
              <label className="pe-f"><span>先頭の文字</span><input className="inp" type="text" value={el.prefix ?? ""} maxLength={10} onChange={(e) => patch({ prefix: e.target.value || undefined } as Partial<PaperEl>)} data-testid="pe-prefix" /></label>
              <label className="pe-f"><span>末尾の文字</span><input className="inp" type="text" value={el.suffix ?? ""} maxLength={10} onChange={(e) => patch({ suffix: e.target.value || undefined } as Partial<PaperEl>)} data-testid="pe-suffix" /></label>
            </div>
          ) : null}
          {isTxt && (el.type === "text" || el.type === "field") ? (
            <>
              <div className="pe-grid2">
                <label className="pe-f"><span>書体</span><select className="inp" value={el.font} onChange={(e) => patch({ font: e.target.value as "serif" | "sans" | "maru" })}>{FONTS.map(([k, n]) => <option key={k} value={k}>{n}</option>)}</select></label>
                <label className="pe-f"><span>大きさ（pt）</span><NumIn value={el.size} step={0.5} min={4} max={72} onChange={(v) => patch({ size: v })} label="文字サイズ" testId="pe-size" /></label>
              </div>
              <div className="pe-row wrap">
                <Seg value={el.bold} options={[[true, "太い"], [false, "ふつう"]]} onChange={(v) => patch({ bold: v })} label="太さ" />
                <Seg value={el.align} options={[["left", "左"], ["center", "中"], ["right", "右"]]} onChange={(v) => patch({ align: v })} label="左右の寄せ" />
                <Seg value={el.valign} options={[["top", "上"], ["middle", "中"], ["bottom", "下"]]} onChange={(v) => patch({ valign: v })} label="上下の寄せ" />
                <span className="hint">色</span>{sw("el.color", "文字")}
              </div>
            </>
          ) : null}
          {(el.type === "line" || el.type === "box") ? (
            <div className="pe-row wrap">
              <select className="inp" style={{ width: 110 }} value={el.style} onChange={(e) => patch({ style: e.target.value as RuleStyle })}>{RULE_STYLES.filter(([v]) => v !== "none" || isBox).map(([v, n]) => <option key={v} value={v}>{n}</option>)}</select>
              <select className="inp" style={{ width: 100 }} value={String(el.lw)} onChange={(e) => patch({ lw: Number(e.target.value) })}>{ruleWidths(el.lw).map((v) => <option key={v} value={String(v)}>{v} mm</option>)}</select>
              <span className="hint">線の色</span>{sw("el.color", "線")}
              {el.type === "box" ? <><span className="hint">塗り</span>{sw("el.fill", "塗り")}<button type="button" className="btn sm" onClick={() => patch({ fill: "" } as Partial<PaperEl>)}>無し</button></> : null}
              {el.type === "line" ? <Seg value={el.dir} options={[["h", "横線"], ["v", "縦線"]]} onChange={(v) => patch(v === "v" ? { dir: v, h: el.h || 30, w: 0 } : { dir: v, w: el.w || 60, h: 0 })} label="線の向き" /> : null}
            </div>
          ) : null}
          {el.type === "image" ? <ImageDetail el={el} patch={patch} layout={layout} H={H} setTab={setTab} /> : null}
          {el.type === "qr" ? (
            <>
              <div className="pe-row wrap">
                <Seg value={el.bind ? "bind" : "text"} options={[["text", "決めた文字"], ["bind", "項目の値"]]} onChange={(v) => patch(v === "bind" ? { bind: "docNo" as PaperBind } : { bind: null })} label="二次元コードの中身" />
                {el.bind ? (
                  <select className="inp" value={el.bind} onChange={(e) => patch({ bind: PaperBind.parse(e.target.value) })} aria-label="二次元コードにする項目" data-testid="pe-qr-bind">
                    {PaperBind.options.filter((b) => b !== "pageNo").map((b) => <option key={b} value={b}>{paperBindLabel(b, currentLabels())}</option>)}
                  </select>
                ) : null}
              </div>
              {!el.bind ? <label className="pe-f"><span>中身の文字（会社の URL など）</span><input className="inp" type="text" value={el.text} maxLength={500} onChange={(e) => patch({ text: e.target.value })} data-testid="pe-qr-text" placeholder="https://" /></label> : null}
            </>
          ) : null}
          {el.type !== "image" && el.type !== "qr" ? (
            <label className="pe-cb"><input type="checkbox" checked={el.frame} onChange={(e) => patch({ frame: e.target.checked })} /> 枠の一部（専用帳票では印刷しない）</label>
          ) : null}
          {el.type !== "line" && el.type !== "box" ? (
            <label className="pe-cb"><input type="checkbox" checked={overlapAllowed(el)} onChange={(e) => patch({ overlap: e.target.checked } as Partial<PaperEl>)} data-testid="pe-overlap" /> 重なりを許可（ほかの部品と重なっても注意に出さない）</label>
          ) : null}
          {el.group ? <p className="hint">組：{el.group}</p> : null}
          {el.locked ? <p className="hint">🔒 法令上、消せません（印字を切ることもできません）。動かすこと・大きさを変えることはできます</p> : null}
        </div>
      ) : null}
      {!el && sel !== "table" ? <p className="hint" style={{ marginTop: 8 }}>紙の上の部品を押すか、一覧から選んでください。</p> : null}
    </>
  );
}

/* ---- 明細表 ---- */
function TableTab({ layout, commit, H, sw, dockBaseY }: Props & { H: (t: string) => React.ReactNode; sw: (t: string, l: string) => React.ReactNode }) {
  const t = layout.table;
  const pre = layout.page.preprinted;
  const set = (pt: Partial<PaperTable>) => commit((c) => updateTable(c, pt));
  const setCol = (i: number, pt: Partial<PaperTable["cols"][number]>) => commit((c) => updateTable(c, (tt) => ({ ...tt, cols: tt.cols.map((col, j) => (j === i ? { ...col, ...pt } : col)) })));
  const setRule = (k: keyof PaperTable["rules"], pt: Partial<PaperTable["rules"]["h"]>) => commit((c) => updateTable(c, (tt) => ({ ...tt, rules: { ...tt.rules, [k]: { ...tt.rules[k], ...pt } } })));
  const pack = () => commit((c) => updateTable(c, (tt) => { let x = 0; return { ...tt, cols: tt.cols.map((col) => { if (!col.on) return col; const n = { ...col, x }; x = r1(x + col.w); return n; }) }; }));
  return (
    <>
      <h3>明細表 {H("位置と幅は紙の上でドラッグしても決められます。列は表の左端からの位置（X）と幅で決めます。列と列のあいだを空けると、専用帳票の枠に合わせられます。")}</h3>
      <div className="pe-grid4">
        <label className="pe-f"><span>左から X</span><NumIn value={t.x} step={0.5} min={0} max={600} onChange={(v) => set({ x: v })} label="明細表の X" /></label>
        <label className="pe-f"><span>上から Y</span><NumIn value={t.y} step={0.5} min={0} max={600} onChange={(v) => set({ y: v })} label="明細表の Y" /></label>
        <label className="pe-f"><span>幅 W</span><NumIn value={t.w} step={0.5} min={20} max={600} onChange={(v) => set({ w: v })} label="明細表の幅" /></label>
        <label className="pe-f"><span>高さ</span><input className="inp pe-num" style={{ width: 72 }} value={r1(dockBaseY - 3 - t.y)} disabled readOnly aria-label="明細表の高さ" /></label>
      </div>
      <h3>行 {H("「ページの下まで空行で埋める」が入っていると、明細が足りない分を罫線だけの空行で埋め、小計・消費税・合計を表の下端に置きます（最初の空行に「明細が終わったあとの文」）。外すと1ページ目は「1ページの行数」の行です（専用帳票の枠に合わせるとき）。")}</h3>
      {layout.kind !== "receipt" ? (
        <label className="pe-cb"><input type="checkbox" checked={t.fill !== false} onChange={(e) => set({ fill: e.target.checked ? undefined : false })} data-testid="pe-fill" /> ページの下まで空行で埋める</label>
      ) : null}
      <div className="pe-grid4">
        <label className="pe-f"><span>1ページの行数</span><NumIn value={t.rows} min={1} max={80} onChange={(v) => set({ rows: Math.round(v) })} label="1ページの行数" testId="pe-rows" /></label>
        <label className="pe-f"><span>行間（mm）</span><NumIn value={t.pitch} step={0.1} min={3} max={30} onChange={(v) => set({ pitch: v })} label="行間" testId="pe-pitch" /></label>
        <label className="pe-f"><span>文字（pt）</span><NumIn value={t.size} step={0.5} min={4} max={30} onChange={(v) => set({ size: v })} label="明細の文字サイズ" /></label>
        <label className="pe-f"><span>文字の色</span>{sw("table.color", "明細の文字")}</label>
      </div>
      <label className="pe-f"><span>明細が終わったあとの文</span><input className="inp" type="text" value={t.restText} onChange={(e) => set({ restText: e.target.value })} /></label>
      <h3>見出し行 {pre ? <span className="pe-tag">専用帳票では印刷しない</span> : null}</h3>
      <div className="pe-row wrap">
        <label className="pe-cb"><input type="checkbox" checked={t.header.on} onChange={(e) => set({ header: { ...t.header, on: e.target.checked } })} /> 印刷する</label>
        <label className="pe-f h"><span>高さ</span><NumIn value={t.header.h} step={0.5} min={3} max={30} onChange={(v) => set({ header: { ...t.header, h: v } })} label="見出し行の高さ" /></label>
        <label className="pe-f h"><span>文字</span><NumIn value={t.header.size} step={0.5} min={4} max={30} onChange={(v) => set({ header: { ...t.header, size: v } })} label="見出しの文字サイズ" /></label>
      </div>
      <div className="pe-row wrap">
        <Seg value={t.header.bold} options={[[true, "太い"], [false, "ふつう"]]} onChange={(v) => set({ header: { ...t.header, bold: v } })} label="見出しの太さ" />
        <span className="hint">文字</span>{sw("table.header.color", "見出しの文字")}
        <span className="hint">地色</span>{sw("table.header.fill", "見出しの地色")}<button type="button" className="btn sm" onClick={() => set({ header: { ...t.header, fill: "" } })}>無し</button>
        <span className="hint">1行おき</span>{sw("table.zebra", "1行おきの地色")}<button type="button" className="btn sm" onClick={() => set({ zebra: "" })}>無し</button>
      </div>
      <h3>列（表の左端からの mm） {H(colLabelOf("name") + "と金額は消せません。「等間隔に配置」は、印刷する列を左から隙間なく並べ直します。見出しの寄せは値と同じです。")}</h3>
      <div className="pe-colhead"><span /><span>見出し</span><span>X</span><span>幅</span><span>文字 pt</span><span>寄せ</span></div>
      {t.cols.map((c, i) => (
        <div key={c.id} className="pe-colrow" data-testid={`pe-col-${c.id}`}>
          <input type="checkbox" checked={c.on} disabled={c.id === "name" || c.id === "amount"} aria-label={`${c.label || colLabelOf(c.id)}を印刷`} onChange={(e) => setCol(i, { on: e.target.checked })} />
          <input className="inp" type="text" value={c.label} aria-label={`${colLabelOf(c.id)}の見出し`} onChange={(e) => setCol(i, { label: e.target.value })} />
          <NumIn value={c.x} step={0.5} min={0} max={600} onChange={(v) => setCol(i, { x: v })} label={`${colLabelOf(c.id)}の X`} width={64} />
          <NumIn value={c.w} step={0.5} min={3} max={600} onChange={(v) => setCol(i, { w: v })} label={`${colLabelOf(c.id)}の幅`} width={64} />
          <NumIn value={c.size} step={0.5} min={4} max={30} onChange={(v) => setCol(i, { size: v })} label={`${colLabelOf(c.id)}の文字`} width={60} />
          <select className="inp" value={c.align} aria-label={`${colLabelOf(c.id)}の寄せ`} onChange={(e) => setCol(i, { align: e.target.value as "left" | "center" | "right", labelAlign: e.target.value as "left" | "center" | "right" })}>
            <option value="left">左</option><option value="center">中</option><option value="right">右</option>
          </select>
        </div>
      ))}
      <div className="pe-row"><button type="button" className="btn sm" onClick={pack}>等間隔に配置</button></div>
      <h3>罫線 {pre ? <span className="pe-tag">専用帳票では印刷しない</span> : null}</h3>
      {([["outer", "外枠"], ["head", "見出しの下"], ["h", "行の間"], ["v", "列の間"]] as Array<[keyof PaperTable["rules"], string]>).map(([k, n]) => {
        const r = t.rules[k];
        return (
          <div key={k} className="pe-rulerow">
            <span>{n}</span>
            <select className="inp" value={r.style} aria-label={`${n}の線の種類`} onChange={(e) => setRule(k, { style: e.target.value as RuleStyle })}>{RULE_STYLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            <select className="inp" value={String(r.w)} disabled={r.style === "none"} aria-label={`${n}の太さ`} onChange={(e) => setRule(k, { w: Number(e.target.value) })}>{ruleWidths(r.w).map((v) => <option key={v} value={String(v)}>{v} mm</option>)}</select>
            {sw(`table.rules.${k}.color`, n)}
          </div>
        );
      })}
    </>
  );
}

/* ---- 用紙 ---- */
function PageTab({ layout, commit, H }: Props & { H: (t: string) => React.ReactNode }) {
  const pg = layout.page;
  const setPg = (pt: Partial<PaperLayout["page"]>) => commit((c) => ({ ...c, page: { ...c.page, ...pt } }));
  const setMargin = (i: number, v: number) => commit((c) => { const m = [...c.page.margin] as [number, number, number, number]; m[i] = v; return { ...c, page: { ...c.page, margin: m } }; });
  const setOffset = (i: number, v: number) => commit((c) => { const o = [...c.page.offset] as [number, number]; o[i] = v; return { ...c, page: { ...c.page, offset: o } }; });
  return (
    <>
      <h3>用紙 {H("B5 や独自の大きさも使えます。「カスタム」で幅と高さを mm で打ちます。向きを変えると幅と高さが入れ替わるだけで、部品は動きません。2ページ目以降の「番号と表だけ」は、2ページ目から「2ページ目以降にも出す」の部品（番号・日付・ページ番号など）と明細表だけを出します。番号・日付は上に寄せ、表はその下から続けます。")}</h3>
      <div className="pe-grid2">
        <label className="pe-f"><span>大きさ</span>
          <select className="inp" value={pg.size} data-testid="pe-size-sel" onChange={(e) => commit((c) => setPageSize(c, e.target.value as PaperLayout["page"]["size"]))}>
            {([["A4", "A4（210×297）"], ["B5", "B5（182×257）"], ["A5", "A5（148×210）"], ["B4", "B4（257×364）"], ["letter", "レター（216×279）"], ["custom", "カスタム"]] as Array<[string, string]>).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
          </select>
        </label>
        <label className="pe-f"><span>向き</span><Seg value={layout.orient} options={[["portrait", "縦"], ["landscape", "横"]]} onChange={(v) => commit((c) => setOrient(c, v))} label="向き" /></label>
      </div>
      <div className="pe-grid2">
        <label className="pe-f"><span>幅（mm）</span><NumIn value={pg.w} min={50} max={600} onChange={(v) => setPg({ w: v, size: "custom" })} label="紙の幅" /></label>
        <label className="pe-f"><span>高さ（mm）</span><NumIn value={pg.h} min={50} max={600} onChange={(v) => setPg({ h: v, size: "custom" })} label="紙の高さ" /></label>
      </div>
      <h3>余白（目安の点線）</h3>
      <div className="pe-grid4">
        {(["上", "右", "下", "左"] as const).map((n, i) => (
          <label key={n} className="pe-f"><span>{n}</span><NumIn value={pg.margin[i] ?? 0} min={0} max={100} onChange={(v) => setMargin(i, v)} label={`余白 ${n}`} /></label>
        ))}
      </div>
      <h3>専用帳票 {H("{pw:formsFrom}から渡された印刷済みの用紙（専用帳票）に、数字と文字だけを打ち込む使い方です。①{pw:formsFrom}の紙を見ながら部品を枠に合わせる（「画像」タブの下敷きが使えます）②「専用帳票モード」を入れると、枠・線・見出しは印刷されず、値だけが出ます ③テスト印刷して、ずれた分を「印字位置の補正」で修正する。枠として扱うかどうかは、部品ごとに「枠の一部」で変えられます。")}</h3>
      <label className="pe-cb"><input type="checkbox" checked={pg.preprinted} data-testid="pe-preprinted" onChange={(e) => setPg({ preprinted: e.target.checked })} /> 専用帳票モード（枠・線・見出しを印刷しない）</label>
      <h3>印字位置の補正（mm） {H("プリンタの癖で全体がずれるときに、印刷だけを右・下にずらします（マイナスで左・上）。画面の位置は変わりません。")}</h3>
      <div className="pe-grid2">
        <label className="pe-f"><span>右へ</span><NumIn value={pg.offset[0]} step={0.5} min={-20} max={20} onChange={(v) => setOffset(0, v)} label="印字位置 右へ" /></label>
        <label className="pe-f"><span>下へ</span><NumIn value={pg.offset[1]} step={0.5} min={-20} max={20} onChange={(v) => setOffset(1, v)} label="印字位置 下へ" /></label>
      </div>
      <h3>2ページ目以降</h3>
      <Seg value={pg.secondPage} options={[["compact", "番号と表だけ"], ["same", "1ページ目と同じ"]]} onChange={(v) => setPg({ secondPage: v })} label="2ページ目以降" />
      {layout.kind === "order" ? (
        <>
          <h3>{t("{order}兼請書")} {H("オンにすると、請書の欄を{order}の紙にも印字します（署名欄）。{vendor}は請書の欄に署名・押印して返送します。オフのときは、請書の欄は{orderAck}の紙だけに印字します（承諾の記録・署名欄）。")}</h3>
          <Switch checked={!!layout.combined} onChange={(v) => commit({ ...layout, combined: v || undefined })} label={t("{order}兼請書")} testId="pe-combined" />
        </>
      ) : null}
    </>
  );
}

/* ---- 色 ---- */
const COLORS_KEY = "gb.paper.colors";
function loadCompanyColors(): string[] {
  try { const v = JSON.parse(localStorage.getItem(COLORS_KEY) ?? "[]"); return Array.isArray(v) ? v.slice(0, 8).map(String) : []; } catch { return []; }
}
function ColorTab({ layout, sel, commit, colorTarget, setColorTarget, H, toast }: Props & { H: (t: string) => React.ReactNode }) {
  const el = sel && sel !== "table" ? layout.els.find((e) => e.id === sel) : undefined;
  const targets: Array<[string, string]> = [];
  if (el) {
    if (el.type === "text" || el.type === "field") targets.push(["el.color", `選んでいる部品「${el.name}」の文字`]);
    if (el.type === "line" || el.type === "box") targets.push(["el.color", `選んでいる部品「${el.name}」の線`]);
    if (el.type === "box") targets.push(["el.fill", `選んでいる部品「${el.name}」の塗り`]);
  }
  for (const [k, n] of Object.entries(COLOR_TARGET_NAME)) targets.push([k, n]);
  const target = targets.some(([k]) => k === colorTarget) ? colorTarget : targets[0]![0];
  const cur = colorGet(layout, sel, target) || "#FFFFFF";
  const [r, g, b] = hex2rgb(cur);
  const [c, m, y, k] = rgb2cmyk(r, g, b);
  const [hex, setHex] = useState(cur);
  const [rgb, setRgb] = useState<[string, string, string]>([String(r), String(g), String(b)]);
  const [cmyk, setCmyk] = useState<[string, string, string, string]>([String(c), String(m), String(y), String(k)]);
  useEffect(() => { setHex(cur); setRgb([String(r), String(g), String(b)]); setCmyk([String(c), String(m), String(y), String(k)]); }, [cur]);
  const [company, setCompany] = useState<string[]>(loadCompanyColors);
  const [recent, setRecent] = useState<string[]>([]);
  const apply = (v: string) => {
    const hx = v.toUpperCase();
    commit((cc) => colorSet(cc, sel, target, hx));
    setRecent((xs) => [hx, ...xs.filter((x) => x !== hx)].slice(0, 12));
  };
  const fromRgb = (vals: [string, string, string]) => { setRgb(vals); const hx = rgb2hex(Number(vals[0]), Number(vals[1]), Number(vals[2])); setHex(hx); const [cc, mm, yy, kk] = rgb2cmyk(...hex2rgb(hx)); setCmyk([String(cc), String(mm), String(yy), String(kk)]); };
  const fromCmyk = (vals: [string, string, string, string]) => { setCmyk(vals); const [rr, gg, bb] = cmyk2rgb(Number(vals[0]), Number(vals[1]), Number(vals[2]), Number(vals[3])); const hx = rgb2hex(rr, gg, bb); setHex(hx); setRgb([String(Math.round(rr)), String(Math.round(gg)), String(Math.round(bb))]); };
  const ratio = contrast(cur, "#FFFFFF");
  const reg = () => {
    const v = /^#?[0-9a-fA-F]{6}$/.test(hex.trim()) ? `#${hex.trim().replace("#", "")}`.toUpperCase() : cur.toUpperCase();
    if (company.includes(v)) return;
    if (company.length >= 8) { toast("会社の色は 8 つまでです"); return; }
    const next = [...company, v];
    setCompany(next);
    try { localStorage.setItem(COLORS_KEY, JSON.stringify(next)); } catch { /* 覚えられなくてもよい */ }
    toast(`会社の色に ${v} を登録しました`);
  };
  const pal = (xs: Array<{ n: string; c: string }>) => (
    <div className="pe-palette">
      {xs.map((x) => <button key={x.c + x.n} type="button" className={cur.toUpperCase() === x.c.toUpperCase() ? "cur" : ""} title={`${x.n} ${x.c}`} aria-label={x.n} onClick={() => apply(x.c)}><i style={{ background: x.c }} /></button>)}
    </div>
  );
  const okHex = /^#?[0-9a-fA-F]{6}$/.test(hex.trim());
  return (
    <>
      <h3>修正中の色</h3>
      <select className="inp" value={target} onChange={(e) => setColorTarget(e.target.value)} aria-label="色を修正する対象">{targets.map(([kk, n]) => <option key={kk} value={kk}>{n}</option>)}</select>
      <div className="pe-colorbig" style={{ background: cur, color: contrast(cur, "#FFFFFF") < 3 ? "#1A1A18" : "#fff" }}>{cur}</div>
      {target.endsWith("color") ? (ratio < 4.5 ? <p className="pe-warn">白い紙の上で読みにくい色です（明度の差 {ratio.toFixed(1)}）</p> : <p className="hint">白い紙の上で読めます（明度の差 {ratio.toFixed(1)}）</p>) : null}
      <h3>{t("{product}の色")}</h3>
      {pal(PAPER_PALETTE.map((x) => ({ n: x.n, c: x.c })))}
      <h3>会社の色 {H("よく使う色を 8 つまでこの端末に覚えます。会社のロゴの色を CMYK で持っていれば、下の C・M・Y・K に打って登録してください。")}</h3>
      {company.length ? pal(company.map((x, i) => ({ n: `会社の色 ${i + 1}`, c: x }))) : <p className="hint">まだありません</p>}
      <h3>最近使った色</h3>
      {recent.length ? pal(recent.map((x) => ({ n: x, c: x }))) : <p className="hint">まだありません</p>}
      <h3>数字で決める {H("CMYK は目安です。画面と PDF は RGB に換算して出します（式：R = 255 × (1−C) × (1−K)）。")}</h3>
      <div className="pe-row wrap">
        <label className="pe-f"><span>HEX</span><input className="inp" type="text" value={hex} style={{ width: 110 }} onChange={(e) => { setHex(e.target.value); if (/^#?[0-9a-fA-F]{6}$/.test(e.target.value.trim())) { const hx = `#${e.target.value.trim().replace("#", "")}`; const [rr, gg, bb] = hex2rgb(hx); setRgb([String(rr), String(gg), String(bb)]); const [cc, mm, yy, kk] = rgb2cmyk(rr, gg, bb); setCmyk([String(cc), String(mm), String(yy), String(kk)]); } }} aria-label="HEX" /></label>
        {(["R", "G", "B"] as const).map((n, i) => (
          <label key={n} className="pe-f"><span>{n}</span><input className="inp" type="text" inputMode="numeric" style={{ width: 60 }} value={rgb[i]} aria-label={n} onChange={(e) => { const v = [...rgb] as [string, string, string]; v[i] = e.target.value; fromRgb(v); }} /></label>
        ))}
      </div>
      <div className="pe-row wrap">
        {(["C", "M", "Y", "K"] as const).map((n, i) => (
          <label key={n} className="pe-f"><span>{n}</span><input className="inp" type="text" inputMode="numeric" style={{ width: 60 }} value={cmyk[i]} aria-label={n} onChange={(e) => { const v = [...cmyk] as [string, string, string, string]; v[i] = e.target.value; fromCmyk(v); }} /></label>
        ))}
        <span className="hint">%</span>
      </div>
      <div className="pe-row">
        <button type="button" className="btn" disabled={!okHex} onClick={() => apply(`#${hex.trim().replace("#", "")}`)}>色適用</button>
        <button type="button" className="btn" onClick={reg}>会社色に登録</button>
      </div>
    </>
  );
}

/* ---- 画像の部品の詳細（046） ---- */
function ImageDetail({ el, patch, layout, H, setTab }: { el: Extract<PaperEl, { type: "image" }>; patch: (pt: Partial<PaperEl>) => void; layout: PaperLayout; H: (t: string) => React.ReactNode; setTab: (t: Tab) => void }) {
  const images = useQuery({ queryKey: IMAGES_QUERY, queryFn: () => api<PaperImageList>("GET", "/paper/images") });
  const items = images.data?.items ?? [];
  const co = images.data?.company ?? { logo: false, seal: false };
  const known = el.asset && (el.asset.startsWith("company:") || items.some((x) => x.id === el.asset));
  return (
    <>
      <label className="pe-f"><span>画像 {H("画像は「画像」タブで上げます。会社のロゴ・社印は自社情報に登録したものが入ります（登録が無ければ印刷されません）。下敷きは紙の上から選べません（部品の一覧から選んで、数字か矢印キーで合わせます）。")}</span>
        <select className="inp" value={el.asset ?? ""} data-testid="pe-image-asset" onChange={(e) => patch({ asset: e.target.value || null } as Partial<PaperEl>)}>
          <option value="">（選んでください）</option>
          <option value="company:logo">会社のロゴ{co.logo ? "" : "（自社情報に未登録）"}</option>
          <option value="company:seal">社印{co.seal ? "" : "（自社情報に未登録）"}</option>
          {items.map((x) => <option key={x.id} value={x.id}>{x.name}（{x.width}×{x.height}）</option>)}
          {el.asset && !known && !images.isPending ? <option value={el.asset}>（消された画像）</option> : null}
        </select>
      </label>
      <div className="pe-row wrap">
        <label className="pe-f h"><span>透過（%）</span><NumIn value={Math.round(el.opacity * 100)} min={0} max={100} step={5} onChange={(v) => patch({ opacity: r1(v / 100) })} label="透過" testId="pe-image-opacity" /></label>
        <label className="pe-cb"><input type="checkbox" checked={el.keep} onChange={(e) => patch({ keep: e.target.checked })} /> 縦横比を保つ</label>
        <button type="button" className="btn sm" onClick={() => setTab("images")}>画像タブへ</button>
      </div>
      <label className="pe-cb"><input type="checkbox" checked={el.guide} data-testid="pe-image-guide" onChange={(e) => patch({ guide: e.target.checked, ...(e.target.checked ? { opacity: Math.min(el.opacity, 0.5) } : {}) })} /> 下敷き（印刷しない。枠に合わせるための目安）</label>
      {el.guide ? (
        <div className="pe-row wrap">
          <button type="button" className="btn sm" onClick={() => patch({ x: 0, y: 0, w: layout.page.w, h: layout.page.h, keep: false })}>用紙いっぱいに</button>
        </div>
      ) : null}
    </>
  );
}

/* ---- 画像タブ（046） ---- */
function ImagesTab({ layout, onAddImage, toast, admin, H }: Props & { H: (t: string) => React.ReactNode }) {
  const qc = useQueryClient();
  const images = useQuery({ queryKey: IMAGES_QUERY, queryFn: () => api<PaperImageList>("GET", "/paper/images") });
  const [busy, setBusy] = useState(false);
  const inputId = useId();
  const used = new Set(layout.els.filter((e): e is Extract<PaperEl, { type: "image" }> => e.type === "image").map((e) => e.asset));
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const img = await prepareImage(file, { maxPx: PAPER_IMAGE_MAX_PX });
      if (!img) { toast(PAPER_IMAGE_MSG.broken); return; }
      const name = file.name.replace(/\.[^.]+$/, "").slice(0, 60) || "画像";
      const res = await fetch(`/api/v1/paper/images?name=${encodeURIComponent(name)}`, { method: "POST", credentials: "include", headers: { "content-type": img.contentType }, body: img.blob });
      if (!res.ok) { const j = (await res.json().catch(() => ({}))) as { title?: string }; toast(j.title ?? "画像を登録できませんでした"); return; }
      qc.setQueryData(IMAGES_QUERY, await res.json());
      toast(img.resized ? `${PAPER_IMAGE_MSG.uploaded}（${img.width}×${img.height} に縮めました）` : PAPER_IMAGE_MSG.uploaded);
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const remove = useMutation({
    mutationFn: (id: string) => api<PaperImageList>("DELETE", `/paper/images/${id}`),
    onSuccess: (v) => { qc.setQueryData(IMAGES_QUERY, v); toast(PAPER_IMAGE_MSG.removed); },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const co = images.data?.company ?? { logo: false, seal: false };
  const items = images.data?.items ?? [];
  const put = (asset: string, name: string, px: { width: number; height: number } | null, guide: boolean) => onAddImage(asset, name, px, guide);
  return (
    <>
      <h3>画像 {H("ロゴ・印影・用紙の模様・{pw:formsFrom}の様式のスキャンを紙に置けます。PNG か JPEG（2MB・2400px まで。大きいものは画面で縮めます）。「下敷き」は印刷しない画像で、{pw:formsFrom}の専用帳票のスキャンを敷いて部品を枠に合わせるためのものです。使っている画像（消した書式・出した紙が指すものも）は消せません。")}</h3>
      {admin ? (
        <div className="pe-row wrap">
          <input id={inputId} className="file-hidden" type="file" accept="image/*" disabled={busy} data-testid="pe-image-file" onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
          <label htmlFor={inputId} className={"filebtn" + (busy ? " is-busy" : "")} aria-disabled={busy || undefined}>{busy ? "登録しています…" : "画像を選択（登録）"}</label>
          <span className="hint">{items.length} / 30 枚</span>
        </div>
      ) : <p className="hint">画像を上げられるのは管理者だけです（置くことはできます）</p>}
      <h3>会社のロゴ・社印 {H("自社情報（設定 → 自社情報）に登録したものです。書式には company:logo／company:seal として入り、登録し直すと紙も変わります（出した紙は変わりません）。")}</h3>
      <div className="pe-imgs">
        {([["company:logo", "会社のロゴ", co.logo], ["company:seal", "社印", co.seal]] as Array<[string, string, boolean]>).map(([asset, name, has]) => (
          <div key={asset} className="pe-img" data-testid={`pe-img-${asset.replace(":", "-")}`}>
            <span className="th">{has ? <img src={assetUrl(asset)} alt="" /> : <span className="hint">無し</span>}</span>
            <span><span className="nm">{name}</span><br /><span className="sub">{has ? (used.has(asset) ? "配置済み" : "自社情報に登録あり") : "自社情報に未登録（置いても印刷されません）"}</span></span>
            <span className="acts"><button type="button" className="btn sm" onClick={() => put(asset, name, null, false)}>配置</button></span>
          </div>
        ))}
      </div>
      <h3>上げた画像</h3>
      {images.isPending ? <p className="hint">読み込んでいます…</p> : null}
      {!images.isPending && !items.length ? <p className="hint">まだありません。「画像選択」で上げてください</p> : null}
      <div className="pe-imgs" data-testid="pe-image-list">
        {items.map((x) => (
          <div key={x.id} className="pe-img" data-testid={`pe-img-${x.id}`}>
            <span className="th"><img src={assetUrl(x.id)} alt="" /></span>
            <span><span className="nm" title={x.name}>{x.name}</span><br /><span className="sub">{x.width}×{x.height}・{Math.max(1, Math.round(x.bytes / 1024))}KB{used.has(x.id) ? "・この書式で使用中" : x.inUse ? "・ほかの書式か出した紙が使用中" : ""}</span></span>
            <span className="acts">
              <button type="button" className="btn sm" onClick={() => put(x.id, x.name, { width: x.width, height: x.height }, false)} data-testid={`pe-img-put-${x.id}`}>配置</button>
              <button type="button" className="btn sm" onClick={() => put(x.id, x.name, { width: x.width, height: x.height }, true)} data-testid={`pe-img-guide-${x.id}`}>下敷きに配置</button>
              {admin ? <button type="button" className="btn sm quiet" disabled={x.inUse || used.has(x.id) || remove.isPending} title={x.inUse || used.has(x.id) ? PAPER_IMAGE_MSG.inUse : ""} onClick={() => { if (window.confirm(`「${x.name}」を消しますか？`)) remove.mutate(x.id); }} data-testid={`pe-img-del-${x.id}`}>削除</button> : null}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

